/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import {Type} from "class-transformer";
import UploadInfoItem from "@/core/components/File/FileUploadProgress/UploadInfoItem";
import {humanFileSize, isObject} from "@/utils/helpers";
import dayjs from 'dayjs'
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import TagItem from "@shared/Tags/TagItem";
import {QwoachFileType} from "./QwoachFileType"
import FilterableItem from "@shared/Filterable/FilterableItem.js";
import {prepareQwoachFileNormalWritePayload} from "@shared/Drive/QwoachFileGeneratedFieldsHelper";

export default class QwoachFile extends FilterableItem{

	//for public read: https://firebasestorage.googleapis.com/v0/b/<bucketName>/o/<urlEncodedFilePath>?alt=media
	// const createPersistentDownloadUrl = (bucket, pathToFile, downloadToken) => {
	// 	return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(
	// 		pathToFile
	// 	)}?alt=media&token=${downloadToken}`;
	// };
	@FilterableProperty()
    name: string = ""
	name_casefolded: string = null //needed to support case insensitive sorting. ref:https://stackoverflow.com/a/48098215/1713920
	path:  string = ""
	url:  string = ""
	size: number = 0
	parentId: string = null
	ownerId: string = null
	@FilterableProperty({includeFullWord: true})
	mimeType: string = null
	isFolder: boolean = false
	isDeleted: boolean = false
	description: string = ""
	metadata: object = {}

	type: QwoachFileType = QwoachFileType.DRIVE_FILE

	@FilterableProperty(tag => tag.title)
	@Type(() => TagItem)
	tagItemsArray: TagItem[] = [];

	isThumbnail: boolean = false

	@Type(() => QwoachFile)
	thumbnailFile: QwoachFile = null


	@Type(() => UploadInfoItem)
	_uploadInfo: UploadInfoItem = new UploadInfoItem()
	_file = null
	_isSelected : boolean = false


	constructor(options = {}) {
		super(options);
		options = this.setupOptions(...arguments)
		this.applyOptions(options)
		this.setName(options.name)
	}

	setupOptions(name: string = null, path: string = null, url: string = null){
		let options = {}
		if(isObject(name)){
			options = name
		} else {
			options.name = name
			options.path = path
			options.url = url
		}
		return options;
	}

	getCurrentSchemaVersion(){
		return 3
	}

	beforeSaveToDb(options = {}) {
		super.beforeSaveToDb(options)
		return prepareQwoachFileNormalWritePayload(this, options)
	}

	migrateSchema(oldVersion, newVersion) {
		if(oldVersion < 2){
			if(!this.mimeType){
				if(this.type === QwoachFileType.LINK){
					this.mimeType = "link"
				} else if(this.isFile()){
					this.mimeType = "doc"
				}
			}
			this.syncNameCaseFolded()
		}
		if(oldVersion < 3){
			let directAdsArray = []
			for(let i = 0; i < this.sharedWithIds.length; i++){
				let id = this.sharedWithIds[i]
				id = QwoachFile.asDirectSharedId(QwoachFile.asInderectSharedId(id))
				directAdsArray.push(id)
			}
			this.sharedWithIds = [...new Set([...this.sharedWithIds, ...directAdsArray])]
		}
		super.migrateSchema(oldVersion, newVersion)
	}


	setName(name = null){
		this.name = name;
		this.syncNameCaseFolded()
	}

	syncNameCaseFolded(){
		this.name_casefolded = this.caseFoldNormalize(this.name)
	}

	setParent(qwoachFile){
    	if(qwoachFile){
			this.parentId = qwoachFile.id
			// this.parentIdArray = [...qwoachFile.parentIdArray, qwoachFile.id]
			// this.parentNameArray = [...qwoachFile.parentNameArray, qwoachFile.name]
		}
	}

	isFile(){
		return !this.isFolder && !this.isLink()
	}

	isLink(){
		return this.type === QwoachFileType.LINK
	}

	isImage() {
		let mimeType = this.mimeType || ""
		return mimeType.startsWith("image/")
	}

	getImageMetadata() {
		return this.metadata?.image || null
	}

	setImageMetadata({width, height} = {}) {
		let imageWidth = Number(width) || 0
		let imageHeight = Number(height) || 0
		let aspectRatio = imageHeight > 0 ? imageWidth / imageHeight : 0
		this.metadata = this.metadata || {}
		this.metadata.image = {
			width: imageWidth,
			height: imageHeight,
			aspectRatio,
		}
	}

	moveToNewClient(oldClientId, newClientId){
		let sharedClientIdIndex = this.sharedWithIds.findIndex(id => id === oldClientId)

		if(sharedClientIdIndex < 0){
			return false
		}
		this.sharedWithIdsOld = this.sharedWithIdsOld || []
		this.sharedWithIdsOld.push(oldClientId)
		this.sharedWithIds[sharedClientIdIndex] = newClientId
		return true
	}

	getAbsoluteUrl(){
		if(!this.url){
			return
		}
		if(this.url.indexOf("://") > 0){
			return this.url
		}
		return `https://${this.url}`
	}

	setIsFolder(isFolder){
		this.isFolder = isFolder
		if(isFolder){
			this.mimeType = null
		}
	}

	sizeFormatted(){
		if (this.isFolder) {
			return "--"
		}
		return humanFileSize(this.size)
	}

	addSharedWithByIdInderect(ids) {
		if(ids){
			return super.addSharedWithById(ids.map(QwoachFile.asInderectSharedId));
		}
		return false
	}

	updatedTsFormatted(){
		let lastModifiedDate = dayjs(this.updatedAt || this.updatedTs)
		let hoursDiff = dayjs().diff(lastModifiedDate, 'hours', true)
		if (hoursDiff < 24) {
			return lastModifiedDate.format("hh:mm a")
		}
		return lastModifiedDate.format("MMM DD, YYYY")
	}

	isParentRootFolder(){
		return this.parentId === null
	}

	setUpdatedNow(){
    	this.updatedAt = new Date().getTime()
	}

	createStorageMetadata(){
		let imageMetadata = this.getImageMetadata()
		let imageWidth = imageMetadata?.width ? String(imageMetadata.width) : ""
		let imageHeight = imageMetadata?.height ? String(imageMetadata.height) : ""
    	return {
    		contentType: this.mimeType || undefined,
			customMetadata: {
    			ownerId: this.ownerId,
				fileId: this.id,
				name: this.name,
				name_casefolded: this.name_casefolded,
				mimeType: this.mimeType,
				parentId: this.parentId,
				isFolder: this.isFolder,
				isDeleted: this.isDeleted,
				description: this.description,
				isThumbnail: this.isThumbnail,
				imageWidth,
				imageHeight,
				sharedWithIdsArrayString: JSON.stringify(this.sharedWithIds)
			}
		}
	}

	// addAssignedTo(baseProfile){
	// 	let isAdded = this.assignedTo.pushUnique(new ProfileShort(baseProfile))
	// 	if(isAdded){
	// 		this.addSharedWithById(baseProfile.id)
	// 	}
	// }

	static createFolder(folderName = "Folder", parentFolder = null) {
		let folder = new QwoachFile(folderName)
		folder.setIsFolder(true)
		folder.setParent(parentFolder)
		return folder
	}

	//this id is used to show first level files in the root of the shared folder
	static asDirectSharedId(id){
		if(id.indexOf("direct_") < 0){
			id = "direct_" + id
		}
		return id
	}

	static asInderectSharedId(id){
		return id.replace("direct_", "")
	}



}
